import React from 'react';
import { Table, Tag, Button, Space, Tooltip, Popconfirm } from 'antd';
import { EditOutlined, EyeOutlined, DeleteOutlined, PoweroffOutlined, CheckCircleOutlined, FileOutlined } from '@ant-design/icons';
import { TermsAndConditionsDTO, ContentFormat } from '../types';

interface TermsAndConditionsTableProps {
  data: TermsAndConditionsDTO[];
  loading?: boolean;
  onView: (record: TermsAndConditionsDTO) => void;
  onEdit: (record: TermsAndConditionsDTO) => void;
  onDelete: (id: string) => void;
  onActivate: (id: string) => void;
  onDeactivate: (id: string) => void;
}

export const TermsAndConditionsTable: React.FC<TermsAndConditionsTableProps> = ({
  data,
  loading = false,
  onView,
  onEdit,
  onDelete,
  onActivate,
  onDeactivate,
}) => {

  console.log({data});
  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: TermsAndConditionsDTO) => (
        <a onClick={() => onView(record)}>
          {text}
          {record.fileId && (
            <Tooltip title="Has document attachment">
              <FileOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
            </Tooltip>
          )}
        </a>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <span>{type.replace(/_/g, ' ')}</span>
      ),
    },
    {
      title: 'Format',
      dataIndex: 'contentFormat',
      key: 'contentFormat',
      render: (format: ContentFormat) => {
        let color = 'blue';
        if (format === ContentFormat.MARKDOWN) {
          color = 'purple';
        } else if (format === ContentFormat.PLAIN_TEXT) {
          color = 'default';
        }
        
        return format ? (
          <Tag color={color}>
            {format.replace(/_/g, ' ')}
          </Tag>
        ) : (
          <Tag color="blue">HTML</Tag> // Default if not specified
        );
      },
    },
    {
      title: 'Entity ID',
      dataIndex: 'entityId',
      key: 'entityId',
      render: (entityId: string) => entityId || '-',
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean) => (
        active ? 
          <Tag color="green">Active</Tag> : 
          <Tag color="orange">Inactive</Tag>
      ),
    },
    {
      title: 'Created Date',
      dataIndex: 'createdDate',
      key: 'createdDate',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: TermsAndConditionsDTO) => (
        <Space size="small">
          <Tooltip title="View">
            <Button 
              icon={<EyeOutlined />} 
              onClick={() => onView(record)} 
              size="small" 
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button 
              icon={<EditOutlined />} 
              onClick={() => onEdit(record)} 
              size="small" 
            />
          </Tooltip>
          {record.active ? (
            <Tooltip title="Deactivate">
              <Popconfirm
                title="Are you sure you want to deactivate this item?"
                onConfirm={() => onDeactivate(record.id!)}
                okText="Yes"
                cancelText="No"
              >
                <Button 
                  icon={<PoweroffOutlined />} 
                  size="small" 
                  danger 
                />
              </Popconfirm>
            </Tooltip>
          ) : (
            <Tooltip title="Activate">
              <Button 
                icon={<CheckCircleOutlined />} 
                onClick={() => onActivate(record.id!)} 
                size="small" 
                type="primary" 
              />
            </Tooltip>
          )}
          <Tooltip title="Delete">
            <Popconfirm
              title="Are you sure you want to delete this item?"
              onConfirm={() => onDelete(record.id!)}
              okText="Yes"
              cancelText="No"
            >
              <Button 
                icon={<DeleteOutlined />} 
                size="small" 
                danger 
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Table 
      columns={columns} 
      dataSource={data} 
      loading={loading}
      rowKey="id"
      pagination={{ pageSize: 10 }}
    />
  );
};
